/**
 * 经纪模块：入驻申请 / 委托发布 / 接单 / 撮合 / 工作台数据
 * 对应 API 清单模块四（/brokers、/broker-orders）
 * 联调对齐（M2-2 遗留 #4）：工作台统计改调后端 /broker-orders/workbench/stats。
 */
import { get, post } from './http';
import {
  mockAcceptBrokerOrder,
  mockApplyBroker,
  mockCancelBrokerOrder,
  mockGetMyBroker,
  mockListBrokerOrders,
  mockMatchBrokerOrder,
  mockPublishBrokerOrder,
  USE_MOCK,
} from './mock';
import type {
  ApplyBrokerParams,
  Broker,
  BrokerOrder,
  PublishBrokerOrderParams,
  WorkbenchStats,
} from '@/types';

export type BrokerOrderView = 'mine' | 'available' | 'matched' | 'cancelled' | 'expired';

/** 经纪入驻申请 */
export async function applyBroker(params: ApplyBrokerParams): Promise<Broker> {
  try {
    return await post<Broker>('/brokers', params);
  } catch (e) {
    if (USE_MOCK.broker) return mockApplyBroker(params);
    throw e;
  }
}

/** 我的经纪档案（未入驻返回 null） */
export async function getMyBroker(): Promise<Broker | null> {
  try {
    const data = await get<{ list: Broker[] }>('/brokers', { user_id: 0 });
    return data.list?.[0] ?? null;
  } catch {
    if (USE_MOCK.broker) return mockGetMyBroker();
    return null;
  }
}

/** 发布经纪委托单 */
export async function publishBrokerOrder(params: PublishBrokerOrderParams): Promise<{ id: number }> {
  try {
    return await post<{ id: number }>('/broker-orders', params);
  } catch (e) {
    if (USE_MOCK.broker) return mockPublishBrokerOrder(params);
    throw e;
  }
}

/** 委托单列表（视角筛选） */
export async function listBrokerOrders(view: BrokerOrderView = 'mine'): Promise<BrokerOrder[]> {
  try {
    const data = await get<{ list: BrokerOrder[] }>('/broker-orders', { view, page: 1, page_size: 100 });
    return data.list ?? [];
  } catch (e) {
    if (USE_MOCK.broker) return mockListBrokerOrders(view);
    throw e;
  }
}

/** 经纪接单 */
export async function acceptBrokerOrder(id: number): Promise<{ ok: boolean }> {
  try {
    return await post<{ ok: boolean }>(`/broker-orders/${id}/accept`);
  } catch (e) {
    if (USE_MOCK.broker) return mockAcceptBrokerOrder(id);
    throw e;
  }
}

/** 撮合完成，生成关联交易订单 */
export async function matchBrokerOrder(id: number): Promise<{ linked_order_id: number }> {
  try {
    return await post<{ linked_order_id: number }>(`/broker-orders/${id}/match`);
  } catch (e) {
    if (USE_MOCK.broker) return mockMatchBrokerOrder(id);
    throw e;
  }
}

/** 取消委托单（后端 handler 需 JSON body，传空对象占位） */
export async function cancelBrokerOrder(id: number): Promise<{ ok: boolean }> {
  try {
    return await post<{ ok: boolean }>(`/broker-orders/${id}/cancel`, {});
  } catch (e) {
    if (USE_MOCK.broker) return mockCancelBrokerOrder(id);
    throw e;
  }
}

/**
 * 工作台聚合数据
 * 联调对齐：改调后端 /broker-orders/workbench/stats（已实现，佣金按佣金记录精确统计），
 * 不再由前端本地估算；接口失败时降级为本地聚合（保留旧逻辑兜底）。
 */
export async function workbenchStats(): Promise<WorkbenchStats> {
  try {
    return await get<WorkbenchStats>('/broker-orders/workbench/stats');
  } catch {
    // 降级：本地聚合（佣金为估算值）
    const [broker, mine, available] = await Promise.all([
      getMyBroker(),
      listBrokerOrders('mine').catch(() => []),
      listBrokerOrders('available').catch(() => []),
    ]);
    const matching = mine.filter((o) => o.status === 1);
    const matched = mine.filter((o) => o.status === 2);
    const commissionTotal = matched.reduce((s, o) => {
      const mid = ((o.price_min ?? 0) + (o.price_max ?? 0)) / 2;
      return s + mid * o.quantity * (o.commission_rate ?? 0.02);
    }, 0);
    return {
      broker,
      available_count: available.length,
      matching_count: matching.length,
      matched_count: matched.length,
      pending_commission: commissionTotal,
      settled_commission: commissionTotal * 0.6,
      commission_total: commissionTotal,
    };
  }
}
